const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

/**
 * Fetch all internship circulars from external API
 * @returns {Promise<Object>} Response object with internship data
 */
export const getInternships = async () => {
  try {
    // Fetch all job-circulars from external API
    const response = await fetch(`${API_BASE_URL}/api/v1/job-circulars`);
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const result = await response.json();
    
    if (result.status === 'success' && result.data && Array.isArray(result.data)) {
      // Filter for internship circular_type and map the data
      const internships = result.data
        .filter(item => {
          const circularType = item.circular_type;
          return circularType === 'internship';
        })
        .map((item) => ({
          id: item.encoded_id,
          title: item.job_title,
          description: item.description || '',
          duration: item.duration || '',
          location: item.location || '',
          department: item.department || '',
          circular_type: item.circular_type,
          deadline: item.deadline || '',
          status: item.status || 'active',
          // Additional fields that might be useful
          requirements: item.requirements || '',
          benefits: item.benefits || '',
          application_deadline: item.application_deadline || item.deadline
        }));
      
      return {
        success: true,
        internships: internships.filter(intern => intern.status !== 'inactive')
      };
    } else {
      return {
        success: true,
        internships: []
      };
    }
  } catch (error) {
    console.error('Error fetching internships:', error);
    return {
      success: false,
      internships: [],
      error: error.message
    };
  }
};

/**
 * Mock internship data for development
 * @returns {Object} Mock response object
 */

/**
 * Submit internship certificate request (Initial request - triggers OTP)
 * @param {Object} requestData - Certificate request data
 * @returns {Promise<Object>} Response object
 */
export const submitCertificateRequest = async (requestData) => {
  try {
    
    // Use Strapi API endpoint which forwards to your Laravel API
    const STRAPI_API_URL = process.env.REACT_APP_API_URL || 'http://localhost:1337/api';
    const response = await fetch(`${STRAPI_API_URL}/external-data/intern-certificate`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        mobile: requestData.mobile,
        program_id: requestData.program_id,
        // Initial request - empty OTP string
        otp: ""
      })
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    return {
      success: result.status,
      message: result.message,
      data: result.data,
      requiresOtp: true // Flag to indicate OTP verification needed
    };
  } catch (error) {
    console.error('Error submitting certificate request:', error);
    return {
      success: false,
      error: error.message,
      message: 'Failed to submit certificate request'
    };
  }
};

/**
 * Verify OTP and get certificate
 * @param {Object} verificationData - OTP verification data
 * @returns {Promise<Object>} Response object
 */
export const verifyOtpAndGetCertificate = async (verificationData) => {
  try {
    const STRAPI_API_URL = process.env.REACT_APP_API_URL || 'http://localhost:1337/api';
    const response = await fetch(`${STRAPI_API_URL}/external-data/intern-certificate`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        mobile: verificationData.mobile,
        program_id: verificationData.program_id,
        otp: verificationData.otp
      })
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    
    if (result.data && result.data.pdf_url) {
      return {
        success: true,
        message: 'Certificate generated successfully!',
        certificateUrl: result.data.pdf_url,
        data: result.data
      };
    } else {
      return {
        success: false,
        message: result.message || 'OTP verification failed',
        error: result.message
      };
    }
  } catch (error) {
    console.error('Error verifying OTP:', error);
    return {
      success: false,
      error: error.message,
      message: 'Failed to verify OTP'
    };
  }
};

/**
 * Resend OTP for certificate request
 * @param {Object} resendData - Resend request data
 * @returns {Promise<Object>} Response object
 */
export const resendCertificateOtp = async (resendData) => {
  try {
    const STRAPI_API_URL = process.env.REACT_APP_API_URL || 'http://localhost:1337/api';
    const response = await fetch(`${STRAPI_API_URL}/external-data/intern-certificate`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        mobile: resendData.mobile,
        program_id: resendData.program_id,
        otp: "",
        resend_otp: true // Laravel expects resend_otp boolean
      })
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    return {
      success: result.status,
      message: result.status ? 'OTP resent to your mobile number' : 'Failed to resend OTP',
      data: result.data
    };
  } catch (error) {
    console.error('Error resending OTP:', error);
    return {
      success: false,
      error: error.message,
      message: 'Failed to resend OTP'
    };
  }
};